import type { RuleCondition } from "@sauron/contracts"
import type { Registry } from "@/api.js"
import { describeConditions, NO_CONDITIONS } from "./condition-text.js"
import { normalize } from "./model.js"

/**
 * What one publish would change, in words.
 *
 * **Editing configuration is a production change**: it takes effect on the next
 * decision, with no cache to purge, and every decision records the version that
 * produced it. A screen that behaves like that has to say what is about to
 * change before it changes it — until now the only way to find out was to
 * publish and compare two rows of a history table.
 *
 * The same function reads history backwards, one version against the one before
 * it, which is the diff the Configuration screen was always meant to have.
 */
export interface ConfigChange {
  /** Where the change lands: the engine, a country, or one rule in a country. */
  readonly scope: string
  readonly label: string
  readonly from: string
  readonly to: string
}

const ABSENT = "—"

const MODE_LABELS: Record<string, string> = {
  veto: "weto",
  score: "punkty",
}

const UNAVAILABLE_LABELS: Record<string, string> = {
  veto: "blokuj (fail-closed)",
  points: "dolicz pełną wagę",
  ignore: "zignoruj",
}

const POLICY_LABELS: Record<string, string> = {
  prepayment: "przedpłata",
  allow: "odroczenie",
}

interface Setting {
  readonly key: string
  readonly label: string
  readonly format: (value: unknown) => string
}

/**
 * The settings every rule has, in the order somebody reads them: what it does
 * before how much it weighs, and where it runs last.
 */
const RULE_SETTINGS: readonly Setting[] = [
  { key: "enabled", label: "Stan", format: (v) => (v === false ? "wyłączona" : "włączona") },
  { key: "mode", label: "Tryb", format: (v) => labelled(MODE_LABELS, v) },
  { key: "weight", label: "Waga", format: (v) => (typeof v === "number" ? `${v} pkt` : ABSENT) },
  {
    key: "onUnavailable",
    label: "Gdy źródło milczy",
    format: (v) => labelled(UNAVAILABLE_LABELS, v),
  },
]

const priorityOfLabel = "Kolejność"

export function describeChanges(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  registry: Registry,
): ConfigChange[] {
  const from = normalize(before, registry)
  const to = normalize(after, registry)
  const changes: ConfigChange[] = []

  push(changes, "Silnik", "Próg blokady", from["blockThreshold"], to["blockThreshold"], (v) =>
    typeof v === "number" ? String(v) : ABSENT,
  )
  push(
    changes,
    "Silnik",
    "Kraj bez zestawu reguł",
    from["uncoveredCountryPolicy"],
    to["uncoveredCountryPolicy"],
    (v) => labelled(POLICY_LABELS, v),
  )

  changes.push(...providerChanges(asRecord(from["providers"]), asRecord(to["providers"]), registry))

  const countriesBefore = asRecord(from["countries"])
  const countriesAfter = asRecord(to["countries"])

  for (const code of union(countriesBefore, countriesAfter)) {
    changes.push(...countryChanges(code, countriesBefore, countriesAfter, registry))
  }

  return changes
}

/**
 * What a source is set to.
 *
 * **Reported by the label a person chose, not by the value stored.** KRD's
 * cache window stores `"30"` and reads *30 dni*; a diff quoting the raw value
 * would make the one line that says "a paid source is now trusted for a month"
 * look like a number nobody set. Only the keys a manifest declares are
 * compared — a key this build cannot render is carried through the publish
 * untouched, so reporting it as a change would be reporting our own round trip.
 */
function providerChanges(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  registry: Registry,
): ConfigChange[] {
  const changes: ConfigChange[] = []

  for (const provider of registry.providers) {
    const from = asRecord(before[provider.providerId])
    const to = asRecord(after[provider.providerId])

    for (const field of provider.fields) {
      push(changes, provider.title, field.label, from[field.key], to[field.key], (value) =>
        value === undefined
          ? ABSENT
          : (field.options?.find((option) => option.value === value)?.label ?? String(value)),
      )
    }
  }

  return changes
}

function countryChanges(
  code: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  registry: Registry,
): ConfigChange[] {
  if (!(code in after)) return [{ scope: code, label: "Zestawy reguł", from: "są", to: "usunięte" }]

  // A whole country arriving or leaving is one line. Reporting every setting
  // of every rule as a change "from nothing" buries the fact that matters.
  if (!(code in before)) return [{ scope: code, label: "Zestawy reguł", from: ABSENT, to: "nowe" }]

  const setsBefore = asRecord(asRecord(before[code])["questions"])
  const setsAfter = asRecord(asRecord(after[code])["questions"])
  const changes: ConfigChange[] = []

  for (const questionId of union(setsBefore, setsAfter)) {
    changes.push(
      ...setChanges(
        code,
        questionTitleOf(questionId, registry),
        questionId in setsBefore ? asRecord(setsBefore[questionId]) : null,
        questionId in setsAfter ? asRecord(setsAfter[questionId]) : null,
        registry,
      ),
    )
  }

  return changes
}

/** One question's rule set in one country: the threshold, then rule by rule. */
function setChanges(
  country: string,
  questionTitle: string,
  from: Record<string, unknown> | null,
  to: Record<string, unknown> | null,
  registry: Registry,
): ConfigChange[] {
  const scope = setScope(country, questionTitle)
  if (to === null) return [{ scope, label: "Zestaw reguł", from: "jest", to: "usunięty" }]
  if (from === null) return [{ scope, label: "Zestaw reguł", from: ABSENT, to: "nowy" }]

  const changes: ConfigChange[] = []

  push(changes, scope, "Próg blokady", from["blockThreshold"], to["blockThreshold"], (v) =>
    typeof v === "number" ? String(v) : ABSENT,
  )

  const rulesBefore = byRuleId(from["rules"])
  const rulesAfter = byRuleId(to["rules"])

  const moved = movedRules(rulesBefore, rulesAfter)

  for (const ruleId of union(rulesBefore, rulesAfter)) {
    changes.push(
      ...ruleChanges(
        ruleScope(country, questionTitle, titleOf(ruleId, registry)),
        asRecord(rulesBefore[ruleId]),
        asRecord(rulesAfter[ruleId]),
        moved.has(ruleId),
        registry,
      ),
    )
  }

  return changes
}

/**
 * Which rules actually moved.
 *
 * **Priorities are derived, so switching one rule off renumbers every rule
 * below it.** Reporting all of that would bury the one change somebody made
 * under a cascade they did not ask for — and it would not even be true to say
 * they reordered anything. What counts is the relative order of the rules that
 * run on both sides; a renumbering that leaves it intact is nobody's edit.
 */
function movedRules(before: Record<string, unknown>, after: Record<string, unknown>): Set<string> {
  const common = Object.keys(before).filter(
    (ruleId) => ruleId in after && runs(before[ruleId]) && runs(after[ruleId]),
  )
  const order = (rules: Record<string, unknown>) =>
    [...common].sort((a, b) => priorityOf(rules[a]) - priorityOf(rules[b]))

  const from = order(before)
  const to = order(after)
  return new Set(from.filter((ruleId, index) => to[index] !== ruleId))
}

function runs(rule: unknown): boolean {
  return asRecord(rule)["enabled"] !== false
}

function priorityOf(rule: unknown): number {
  const value = asRecord(rule)["priority"]
  return typeof value === "number" ? value : 0
}

function ruleChanges(
  scope: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  moved: boolean,
  registry: Registry,
): ConfigChange[] {
  const changes: ConfigChange[] = []

  for (const setting of RULE_SETTINGS) {
    push(changes, scope, setting.label, before[setting.key], after[setting.key], setting.format)
  }

  // Conditions are an array, so `Object.is` never says they match — the guard
  // that carries this is `push`'s: two values that print the same are not a
  // change. Rendering them into one sentence is therefore load-bearing, not
  // presentation.
  push(changes, scope, "Warunki", before["conditions"], after["conditions"], (value) =>
    Array.isArray(value)
      ? describeConditions(value as RuleCondition[], registry.conditions)
      : NO_CONDITIONS,
  )

  if (moved) {
    push(changes, scope, priorityOfLabel, before["priority"], after["priority"], (v) =>
      typeof v === "number" ? `#${v}` : ABSENT,
    )
  }

  const paramsBefore = asRecord(before["params"])
  const paramsAfter = asRecord(after["params"])
  for (const key of union(paramsBefore, paramsAfter)) {
    push(changes, scope, key, paramsBefore[key], paramsAfter[key], plain)
  }

  return changes
}

/**
 * How a change names the place it lands. The rule list reads changes back by
 * this name to mark the rules a draft has touched, so the spelling lives here
 * and nowhere else — a scope written by hand in two places would drift and
 * the mark would silently stop appearing. It did, in this very file: the day
 * `ruleScope` was written, `setChanges` still spelt the rule scope by hand a
 * few lines above it (2026-09-15, found in audit), and the list's test fed it
 * a `ruleScope`-built change, so nothing could have noticed. `RulesPane.test`
 * now marks a rule from `describeChanges` itself.
 */
export function setScope(country: string, questionTitle: string): string {
  return `${country} · ${questionTitle}`
}

export function ruleScope(country: string, questionTitle: string, ruleTitle: string): string {
  return `${setScope(country, questionTitle)} · ${ruleTitle}`
}

function push(
  changes: ConfigChange[],
  scope: string,
  label: string,
  before: unknown,
  after: unknown,
  format: (value: unknown) => string,
): void {
  if (Object.is(before, after)) return
  const from = format(before)
  const to = format(after)
  // Two values that print the same are not a change anybody can act on, and a
  // row reading "punkty → punkty" is worse than no row.
  if (from === to) return
  changes.push({ scope, label, from, to })
}

function labelled(labels: Record<string, string>, value: unknown): string {
  if (typeof value !== "string") return ABSENT
  return labels[value] ?? value
}

function plain(value: unknown): string {
  if (value === undefined || value === null) return ABSENT
  if (typeof value === "number") return value.toLocaleString("pl-PL")
  if (typeof value === "boolean") return value ? "tak" : "nie"
  return String(value)
}

function titleOf(ruleId: string, registry: Registry): string {
  return registry.rules.find((rule) => rule.ruleId === ruleId)?.title ?? ruleId
}

function questionTitleOf(questionId: string, registry: Registry): string {
  return (
    registry.questions.find((question) => question.questionId === questionId)?.title ?? questionId
  )
}

function byRuleId(rules: unknown): Record<string, unknown> {
  if (!Array.isArray(rules)) return {}
  const found: Record<string, unknown> = {}
  for (const entry of rules) {
    if (isRecord(entry) && typeof entry["ruleId"] === "string") found[entry["ruleId"]] = entry
  }
  return found
}

/** Keys of both sides, the first side's order first: a removal reads in place. */
function union(before: Record<string, unknown>, after: Record<string, unknown>): string[] {
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
}

function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}
